// @flow
import cloneDeep from 'lodash-es/cloneDeep';
import set from 'lodash-es/set';

import { createDuck } from '@rover/react-lib/lib/redux-duck';
import type {
  ActionCreator,
  EffectCreator,
  SelectorCreator,
  DuckOptions,
  Duck,
} from '@rover/react-lib/lib/redux-duck';
import type { FetchFrontendConfigurationResponse, StrictStringMap } from '@rover/react-lib/types';
import fetchFrontendConfigurationResponseFactory from '@rover/react-lib/factories/fetchFrontendConfigurationResponseFactory';
import { get, setLanguage } from '@rover/react-lib/apis/frontendConfiguration.api';
import reportError from '@rover/utilities/reportError';
import getDateTimeFormatMapForLang from '@rover/shared/js/constants/i18n/datetime';
import { DEFAULT_LANG } from '@rover/shared/js/constants/i18n/language';

import generateActions from './generateActions';

const DEFAULT_CC_TLD = 'US';

export type State = {
  errorMessage: string,
  isLoading: boolean,
  fetchFrontendConfigurationResponse: FetchFrontendConfigurationResponse,
};

type Actions = {
  fetchFrontendConfigurationPending: ActionCreator<void>,
  fetchFrontendConfigurationFailure: ActionCreator<{ errorMessage: string }>,
  fetchFrontendConfigurationSuccess: ActionCreator<FetchFrontendConfigurationResponse>,
  setLanguage: ActionCreator<{ language: string }>,
};

type Effects = {
  fetchFrontendConfiguration: EffectCreator<void>,
  setLanguage: EffectCreator<{ language: string }>,
};

type Selectors = {
  getFrontendConfiguration: SelectorCreator<FetchFrontendConfigurationResponse>,
  getFlags: SelectorCreator<{ [string]: boolean }>,
  getSettings: SelectorCreator<{ [string]: any }>,
  isFlagActive: SelectorCreator<(string) => boolean>,
  getDateTimeFormatMap: SelectorCreator<StrictStringMap>,
  getLanguage: SelectorCreator<string>,
  getCcTld: SelectorCreator<string>,
  getIsBeforeHoliday: SelectorCreator<boolean>,
  getCsrfCookieName: SelectorCreator<boolean>,
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'frontendConfiguration',
  initialState: {
    isLoading: false,
    errorMessage: '',
    fetchFrontendConfigurationResponse: fetchFrontendConfigurationResponseFactory(),
  },
  // $FlowFixMe
  actions: {
    ...generateActions('fetchFrontendConfiguration'),
    setLanguage: ({ language }) => state =>
      set(cloneDeep(state), 'fetchFrontendConfigurationResponse.settings.language', language),
  },
  effects: {
    fetchFrontendConfiguration: () => async (
      dispatch,
      getState,
      {
        fetchFrontendConfigurationPending,
        fetchFrontendConfigurationSuccess,
        fetchFrontendConfigurationFailure,
      },
      effects,
      selectors,
      { expressParams }
    ) => {
      dispatch(fetchFrontendConfigurationPending());
      try {
        const fetchFrontendConfigurationResponse: FetchFrontendConfigurationResponse = await get(
          expressParams
        );
        dispatch(fetchFrontendConfigurationSuccess(fetchFrontendConfigurationResponse));
      } catch (e) {
        dispatch(fetchFrontendConfigurationFailure(e.message));
        reportError(e);
      }
    },
    setLanguage: ({ language }) => async (dispatch, getState, duckActions) => {
      dispatch(duckActions.setLanguage({ language }));

      await setLanguage({ language });

      // Note - we need to refresh the page here in order to make sure everything
      // gets rerendered in the selected language
      // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
      if (window && window.location) {
        // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
        window.location.reload();
      }
    },
  },
  selectors: {
    getFrontendConfiguration: (getState, createSelector) =>
      createSelector(
        [getState],
        x => x.fetchFrontendConfigurationResponse
      ),
    getFlags: (getState, createSelector, { getFrontendConfiguration }) =>
      createSelector(
        [getFrontendConfiguration],
        x => x.flags
      ),
    getSettings: (getState, createSelector, { getFrontendConfiguration }) =>
      createSelector(
        [getFrontendConfiguration],
        x => x.settings
      ),
    isFlagActive: (getState, createSelector, { getFlags }) =>
      createSelector(
        [getFlags],
        flags => flagToCheck => !!flags[flagToCheck]
      ),
    getLanguage: (getState, createSelector, { getSettings }) =>
      createSelector(
        [getSettings],
        settings => settings.language || DEFAULT_LANG
      ),
    getDateTimeFormatMap: (getState, createSelector, { getLanguage }) =>
      createSelector(
        [getLanguage],
        language => getDateTimeFormatMapForLang(language)
      ),
    getCcTld: (getState, createSelector, { getSettings }) =>
      createSelector(
        [getSettings],
        settings => settings.ccTld || DEFAULT_CC_TLD
      ),
    getIsBeforeHoliday: (getState, createSelector, { getSettings }) =>
      createSelector(
        [getSettings],
        settings => settings.isBeforeHoliday
      ),
    getCsrfCookieName: (getState, createSelector, { getSettings }) =>
      createSelector(
        [getSettings],
        settings => settings.getCsrfCookieName
      ),
  },
};

export const { actions, selectors, effects, reducer } = (createDuck(options): Duck<
  State,
  Actions,
  Effects,
  Selectors
>);



// WEBPACK FOOTER //
// ../react-lib/src/redux/ducks/frontendConfiguration.duck.js